/**
 * youtubeEmbed.js
 * Video URL detection, YouTube/Vimeo lazy embeds, and direct video player enhancement
 */

import { createVideoPlayer } from './videoPlayer.js?v=302';

// ═══════════════════════════════════════════════════════════
// URL PATTERNS
// ═══════════════════════════════════════════════════════════

/**
 * YouTube URL patterns:
 * - youtube.com/watch?v=ID
 * - youtu.be/ID
 * - youtube.com/embed/ID
 * - youtube.com/shorts/ID
 */
const YT_PATTERNS = [
  /(?:youtube\.com\/watch\?.*v=)([\w-]{11})/i,
  /(?:youtu\.be\/)([\w-]{11})/i,
  /(?:youtube\.com\/embed\/)([\w-]{11})/i,
  /(?:youtube\.com\/shorts\/)([\w-]{11})/i,
];

/** Vimeo URL pattern: vimeo.com/ID */
const VIMEO_PATTERN = /(?:vimeo\.com\/)(\d+)/i;

/** Direct video file extensions */
const VIDEO_EXT_REGEX = /\.(mp4|webm|ogg)(\?.*)?$/i;

/** MIME types by extension */
const MIME_MAP = {
  mp4: 'video/mp4',
  webm: 'video/webm',
  ogg: 'video/ogg',
};

// ═══════════════════════════════════════════════════════════
// PUBLIC URL HELPERS
// ═══════════════════════════════════════════════════════════

/**
 * Check if a URL is a known video platform URL (YouTube or Vimeo)
 * @param {string} url
 * @returns {boolean}
 */
export function isVideoUrl(url) {
  if (!url) return false;
  return YT_PATTERNS.some((r) => r.test(url)) || VIMEO_PATTERN.test(url);
}

/**
 * Check if a URL points to a direct video file (.mp4, .webm, .ogg)
 * @param {string} url
 * @returns {boolean}
 */
export function isDirectVideoUrl(url) {
  if (!url) return false;
  try {
    const pathname = new URL(url).pathname;
    return VIDEO_EXT_REGEX.test(pathname);
  } catch {
    return VIDEO_EXT_REGEX.test(url);
  }
}

// ═══════════════════════════════════════════════════════════
// ID EXTRACTION
// ═══════════════════════════════════════════════════════════

/**
 * Extract YouTube video ID from URL
 * @param {string} url
 * @returns {string|null}
 */
function extractYouTubeId(url) {
  for (const pattern of YT_PATTERNS) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

/**
 * Extract Vimeo video ID from URL
 * @param {string} url
 * @returns {string|null}
 */
function extractVimeoId(url) {
  const match = url.match(VIMEO_PATTERN);
  return match ? match[1] : null;
}

/**
 * Get file extension from URL path
 * @param {string} url
 * @returns {string}
 */
function getVideoExt(url) {
  try {
    const pathname = new URL(url).pathname;
    const match = pathname.match(/\.(mp4|webm|ogg)/i);
    return match ? match[1].toLowerCase() : 'mp4';
  } catch {
    return 'mp4';
  }
}

// ═══════════════════════════════════════════════════════════
// EMBED CREATION
// ═══════════════════════════════════════════════════════════

/**
 * Create a lazy-load YouTube embed element (thumbnail + play button, iframe on click)
 * @param {string} videoId
 * @param {string} originalUrl
 * @returns {HTMLElement}
 */
function createYouTubeEmbed(videoId, originalUrl) {
  const wrapper = document.createElement('div');
  wrapper.className = 'media-video-embed media-video-embed--youtube';
  wrapper.dataset.videoId = videoId;
  wrapper.dataset.videoUrl = originalUrl;

  // Thumbnail
  const thumb = document.createElement('div');
  thumb.className = 'media-video-embed-thumb';

  const img = document.createElement('img');
  img.className = 'media-video-embed-thumb-img';
  img.alt = 'YouTube video thumbnail';
  img.loading = 'lazy';
  img.decoding = 'async';
  // Try maxresdefault, fallback to hqdefault
  img.src = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  img.addEventListener('error', () => {
    if (!img.dataset.fallback) {
      img.dataset.fallback = '1';
      img.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
  }, { once: false });

  thumb.appendChild(img);

  // Play button overlay
  const playOverlay = document.createElement('button');
  playOverlay.className = 'media-video-embed-play';
  playOverlay.setAttribute('aria-label', 'Play YouTube video');
  playOverlay.innerHTML = `
    <svg class="media-video-embed-play-icon" viewBox="0 0 68 48" width="68" height="48">
      <path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79 0 34 0 34 0S12.21 0 6.9 1.55c-2.93.78-4.64 3.26-5.42 6.19C0 13.05 0 24 0 24s0 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 48 34 48 34 48s21.79 0 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C68 34.95 68 24 68 24s0-10.95-1.48-16.26z" fill="var(--accent-primary)" fill-opacity="0.9"/>
      <path d="M45 24L27 14v20" fill="var(--foreground)"/>
    </svg>
  `;

  // YouTube badge
  const badge = document.createElement('span');
  badge.className = 'media-video-embed-badge';
  badge.textContent = 'YouTube';

  wrapper.appendChild(thumb);
  wrapper.appendChild(playOverlay);
  wrapper.appendChild(badge);

  // Click to load iframe
  playOverlay.addEventListener('click', () => loadYouTubeIframe(wrapper, videoId));
  thumb.addEventListener('click', () => loadYouTubeIframe(wrapper, videoId));

  return wrapper;
}

/**
 * Replace the thumbnail with an actual YouTube iframe
 * @param {HTMLElement} wrapper
 * @param {string} videoId
 */
function loadYouTubeIframe(wrapper, videoId) {
  const iframe = document.createElement('iframe');
  iframe.className = 'media-video-embed-iframe';
  iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('allowfullscreen', '');
  iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
  iframe.setAttribute('loading', 'lazy');
  iframe.setAttribute('title', 'YouTube video player');

  wrapper.innerHTML = '';
  wrapper.classList.add('media-video-embed--loaded');
  wrapper.appendChild(iframe);
}

/**
 * Create a lazy-load Vimeo embed element
 * @param {string} videoId
 * @param {string} originalUrl
 * @returns {HTMLElement}
 */
function createVimeoEmbed(videoId, originalUrl) {
  const wrapper = document.createElement('div');
  wrapper.className = 'media-video-embed media-video-embed--vimeo';
  wrapper.dataset.videoId = videoId;
  wrapper.dataset.videoUrl = originalUrl;

  // Thumbnail placeholder
  const thumb = document.createElement('div');
  thumb.className = 'media-video-embed-thumb';

  const img = document.createElement('img');
  img.className = 'media-video-embed-thumb-img';
  img.alt = 'Vimeo video thumbnail';
  img.loading = 'lazy';
  img.decoding = 'async';
  thumb.appendChild(img);

  // Async Vimeo thumbnail fetch
  fetchVimeoThumbnail(videoId).then((thumbUrl) => {
    if (thumbUrl) img.src = thumbUrl;
  });

  // Play button overlay
  const playOverlay = document.createElement('button');
  playOverlay.className = 'media-video-embed-play';
  playOverlay.setAttribute('aria-label', 'Play Vimeo video');
  playOverlay.innerHTML = `
    <svg class="media-video-embed-play-icon" viewBox="0 0 68 48" width="68" height="48">
      <path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79 0 34 0 34 0S12.21 0 6.9 1.55c-2.93.78-4.64 3.26-5.42 6.19C0 13.05 0 24 0 24s0 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 48 34 48 34 48s21.79 0 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C68 34.95 68 24 68 24s0-10.95-1.48-16.26z" fill="var(--accent-primary)" fill-opacity="0.9"/>
      <path d="M45 24L27 14v20" fill="var(--foreground)"/>
    </svg>
  `;

  // Vimeo badge
  const badge = document.createElement('span');
  badge.className = 'media-video-embed-badge';
  badge.textContent = 'Vimeo';

  wrapper.appendChild(thumb);
  wrapper.appendChild(playOverlay);
  wrapper.appendChild(badge);

  // Click to load iframe
  playOverlay.addEventListener('click', () => loadVimeoIframe(wrapper, videoId));
  thumb.addEventListener('click', () => loadVimeoIframe(wrapper, videoId));

  return wrapper;
}

/**
 * Fetch thumbnail URL from Vimeo oEmbed API
 * @param {string} videoId
 * @returns {Promise<string|null>}
 */
async function fetchVimeoThumbnail(videoId) {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(
      `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=640`,
      { signal: controller.signal, credentials: 'omit' }
    );
    clearTimeout(timeoutId);
    if (!res.ok) return null;
    const data = await res.json();
    return data.thumbnail_url || null;
  } catch {
    return null;
  }
}

/**
 * Replace the thumbnail with an actual Vimeo iframe
 * @param {HTMLElement} wrapper
 * @param {string} videoId
 */
function loadVimeoIframe(wrapper, videoId) {
  const iframe = document.createElement('iframe');
  iframe.className = 'media-video-embed-iframe';
  iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1&dnt=1`;
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('allowfullscreen', '');
  iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
  iframe.setAttribute('loading', 'lazy');
  iframe.setAttribute('title', 'Vimeo video player');

  wrapper.innerHTML = '';
  wrapper.classList.add('media-video-embed--loaded');
  wrapper.appendChild(iframe);
}

// ═══════════════════════════════════════════════════════════
// MAIN ENHANCER
// ═══════════════════════════════════════════════════════════

/**
 * Enhance a rendered message element by detecting video URLs and replacing/adding embeds.
 * Idempotent: safe to call repeatedly on the same element.
 *
 * @param {HTMLElement} messageEl - Message bubble or container with .message-content
 */
export function enhanceVideoLinks(messageEl) {
  if (!messageEl || !(messageEl instanceof HTMLElement)) return;

  // Idempotency: skip if already processed
  if (messageEl.dataset.videoEnhanced === '1') return;

  const contentEl = messageEl.querySelector('.message-content') || messageEl;
  const urls = collectVideoUrls(contentEl);

  if (urls.length === 0) {
    return;
  }

  // Remove any existing embed containers from a previous run
  // (can happen when flag is cleared during streaming re-renders)
  const parent = contentEl.parentElement || messageEl;
  const oldEmbeds = parent.querySelectorAll('.media-video-embed-list');
  oldEmbeds.forEach((el) => el.remove());

  // Container for all video embeds
  const embedContainer = document.createElement('div');
  embedContainer.className = 'media-video-embed-list';

  for (const urlInfo of urls) {
    let embed = null;

    if (urlInfo.type === 'youtube') {
      embed = createYouTubeEmbed(urlInfo.id, urlInfo.url);
    } else if (urlInfo.type === 'vimeo') {
      embed = createVimeoEmbed(urlInfo.id, urlInfo.url);
    } else if (urlInfo.type === 'direct') {
      const ext = getVideoExt(urlInfo.url);
      embed = createVideoPlayer(urlInfo.url, {
        type: MIME_MAP[ext] || 'video/mp4',
      });
    }

    if (embed) {
      embedContainer.appendChild(embed);
    }
  }

  if (embedContainer.children.length > 0) {
    // Place embeds after content, inside the bubble
    if (contentEl.parentElement) {
      contentEl.insertAdjacentElement('afterend', embedContainer);
    } else {
      messageEl.appendChild(embedContainer);
    }
  }

  messageEl.dataset.videoEnhanced = '1';
}

/**
 * Collect all video URLs from a content element
 * @param {HTMLElement} rootEl
 * @returns {Array<{url: string, type: string, id: string|null}>}
 */
function collectVideoUrls(rootEl) {
  const results = [];
  const seen = new Set();

  /**
   * Process a candidate URL
   * @param {string} rawUrl
   */
  function processUrl(rawUrl) {
    if (!rawUrl) return;
    let url;
    try {
      url = new URL(rawUrl.trim());
    } catch {
      return;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

    const href = url.href;
    if (seen.has(href)) return;
    seen.add(href);

    // YouTube
    const ytId = extractYouTubeId(href);
    if (ytId) {
      results.push({ url: href, type: 'youtube', id: ytId });
      return;
    }

    // Vimeo
    const vimeoId = extractVimeoId(href);
    if (vimeoId) {
      results.push({ url: href, type: 'vimeo', id: vimeoId });
      return;
    }

    // Direct video
    if (isDirectVideoUrl(href)) {
      results.push({ url: href, type: 'direct', id: null });
    }
  }

  // Check <a> tags first
  rootEl.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') || '';
    if (href.startsWith('http://') || href.startsWith('https://')) {
      processUrl(href);
    }
  });

  // Fallback: regex scan for bare URLs in text
  const text = rootEl.textContent || '';
  const urlRegex = /https?:\/\/[^\s<]+/gi;
  const matches = text.match(urlRegex) || [];
  matches.forEach((m) => processUrl(m));

  return results;
}
